import { Badge } from '@eriko/ui/src/components/ui/badge';
import { Button } from '@eriko/ui/src/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@eriko/ui/src/components/ui/card';
import { Loader2 } from 'lucide-react';
import {
  use1099Prep,
  useFilingDrafts,
  useMarkFiled,
  useScheduleCSummary,
  useTaxDeadlines,
} from '../../../../hooks/useTax';

function formatDollars(minor: number): string {
  return `$${(minor / 100).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function filingTypeLabel(type: string): string {
  const map: Record<string, string> = {
    SCHEDULE_C: 'Schedule C',
    FORM_1099: 'Form 1099-NEC',
    VAT_RETURN: 'VAT Return',
    SA103: 'SA103 Self Assessment',
    CIS300: 'CIS Monthly Return',
    CT600_SUMMARY: 'CT600 Corporation Tax',
  };
  return map[type] ?? type;
}

function statusLabel(s: string): string {
  const map: Record<string, string> = {
    DRAFT: 'Draft',
    READY: 'Ready',
    FILED_VIA_INTEGRATION: 'Filed',
    FILED_EXTERNALLY: 'Filed',
    ARCHIVED: 'Archived',
  };
  return map[s] ?? s;
}

export function UsTaxPanel({ orgId }: { orgId: string }) {
  const year = new Date().getFullYear();
  const { data: prep1099, isLoading: loading1099 } = use1099Prep(orgId, year);
  const { data: scheduleC, isLoading: loadingScheduleC } = useScheduleCSummary(orgId, year);
  const { data: filingDrafts, isLoading: loadingDrafts } = useFilingDrafts(orgId);
  const { data: deadlines, isLoading: loadingDeadlines } = useTaxDeadlines(orgId);
  const markFiled = useMarkFiled(orgId);

  const isLoading = loading1099 || loadingScheduleC || loadingDrafts || loadingDeadlines;

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-surface-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Deadlines */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Upcoming Deadlines</CardTitle>
        </CardHeader>
        <CardContent>
          {!deadlines || deadlines.length === 0 ? (
            <p className="text-sm text-muted-foreground">No upcoming deadlines.</p>
          ) : (
            <div className="space-y-2">
              {deadlines.map((d) => (
                <div key={d.filingType} className="flex items-center justify-between text-sm">
                  <span className="text-surface-700">{d.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-surface-500">{formatDate(d.dueDate)}</span>
                    <Badge
                      variant={
                        d.daysUntilDue <= 7
                          ? 'danger'
                          : d.daysUntilDue <= 30
                            ? 'warning'
                            : 'success'
                      }
                    >
                      {d.daysUntilDue} days
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 1099 Prep Status */}
      {prep1099 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">1099 Prep — {year}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-surface-900">
                  {prep1099.totalContractors}
                </div>
                <div className="text-xs text-surface-500">Contractors</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">{prep1099.readyToFile}</div>
                <div className="text-xs text-surface-500">Ready to file</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-amber-600">{prep1099.needsAttention}</div>
                <div className="text-xs text-surface-500">Needs attention</div>
              </div>
            </div>
            {prep1099.contractors.length > 0 && (
              <div className="mt-4 space-y-1">
                {prep1099.contractors.slice(0, 5).map((c) => (
                  <div
                    key={c.contractorId}
                    className="flex items-center justify-between text-sm py-1"
                  >
                    <span className="text-surface-700 truncate flex-1">{c.legalName}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-surface-500">{formatDollars(c.totalPaidMinor)}</span>
                      {c.exceedsThreshold ? (
                        <Badge variant="danger" className="text-xs">
                          Over $600
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">
                          Under $600
                        </Badge>
                      )}
                      {c.w9Status === 'missing' && (
                        <Badge variant="warning" className="text-xs">
                          No W-9
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Schedule C Summary */}
      {scheduleC && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Schedule C YTD — {year}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center mb-4">
              <div>
                <div className="text-lg font-bold text-surface-900">
                  {formatDollars(scheduleC.grossReceiptsMinor)}
                </div>
                <div className="text-xs text-surface-500">Gross Receipts</div>
              </div>
              <div>
                <div className="text-lg font-bold text-surface-900">
                  {formatDollars(scheduleC.totalExpensesMinor)}
                </div>
                <div className="text-xs text-surface-500">Total Expenses</div>
              </div>
              <div>
                <div className="text-lg font-bold text-surface-900">
                  {formatDollars(scheduleC.netProfitMinor)}
                </div>
                <div className="text-xs text-surface-500">Net Profit</div>
              </div>
            </div>
            <div className="space-y-1">
              {scheduleC.lines
                .filter((l) => l.totalMinor !== 0)
                .map((l) => (
                  <div key={l.ref} className="flex items-center justify-between text-sm py-0.5">
                    <span className="text-surface-600">
                      L{l.lineNumber}: {l.displayName}
                    </span>
                    <span className="text-surface-900 font-medium">
                      {formatDollars(Math.abs(l.totalMinor))}
                    </span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filing Drafts */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filing Drafts</CardTitle>
        </CardHeader>
        <CardContent>
          {!filingDrafts || filingDrafts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No filing drafts yet.</p>
          ) : (
            <div className="space-y-2">
              {filingDrafts.map((d) => (
                <div key={d.id} className="flex items-center justify-between text-sm py-1">
                  <div>
                    <span className="text-surface-700 font-medium">{filingTypeLabel(d.filingType)}</span>
                    <span className="text-surface-500 ml-2">
                      {formatDate(d.periodStart)} — {formatDate(d.periodEnd)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {d.source && (
                      <Badge
                        variant="outline"
                        className="bg-purple-50 text-purple-700 border-purple-200"
                        title="Imported from an uploaded tax form (not computed from transactions)"
                      >
                        Imported
                      </Badge>
                    )}
                    <Badge
                      variant="outline"
                      className={
                        d.status === 'DRAFT'
                          ? ''
                          : d.status === 'READY'
                            ? 'bg-blue-50 text-blue-700 border-blue-200'
                            : 'bg-green-50 text-green-700 border-green-200'
                      }
                    >
                      {statusLabel(d.status)}
                    </Badge>
                    {(d.status === 'DRAFT' || d.status === 'READY') && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={markFiled.isPending}
                        onClick={() =>
                          markFiled.mutate({
                            draftId: d.id,
                            filedVia: 'manual',
                          })
                        }
                      >
                        Mark filed
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
